import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ShieldAlert, ChevronDown, ChevronUp, BadgeAlert, Globe, Wrench, HardHat } from "lucide-react";
import { permisExpiringSoon } from "../pages/Personal";

// Categorii PSA cu mărime — singurele relevante pentru alerte de stoc (per mărime)
const PSA_SIZED_CATEGORIES = new Set(["casca", "cizme", "hanorac", "tricou", "geaca", "bocanci", "pantaloni"]);
const PSA_CAT_LABEL = {
  casca: "Căști",
  cizme: "Cizme", hanorac: "Hanorace", tricou: "Tricouri",
  geaca: "Geci", bocanci: "Bocanci", pantaloni: "Pantaloni",
};

/**
 * Banner unificat de compliance pentru Dashboard admin.
 * Afișează în UN SINGUR LOC alertele active care necesită acțiune azi:
 *   1) Permise de ședere elvețiene EXPIRATE sau care expiră ≤14 zile
 *   2) Subcontractori cu Meldeverfahren < 10 zile rămase (din 90)
 *   3) Daune cu status open/in_progress
 *
 * Endpoints folosite (toate existente):
 *   - GET /api/persons
 *   - GET /api/employer-companies
 *   - GET /api/meldungen/summary?year=YYYY
 *   - GET /api/damages?status=open  +  status=in_progress
 */
export default function ComplianceBanner() {
  const { isAdmin } = useAuth();
  const [permits, setPermits] = useState([]);
  const [meldungen, setMeldungen] = useState([]);
  const [damages, setDamages] = useState([]);
  const [psaAlerts, setPsaAlerts] = useState([]); // [{cat, size, qty, level: 'out'|'low'}]
  const [expanded, setExpanded] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const year = new Date().getFullYear();
        const [persons, employers, meld, dOpen, dProg, inv] = await Promise.all([
          api.get("/persons").catch(() => ({ data: [] })),
          api.get("/employer-companies").catch(() => ({ data: [] })),
          api.get(`/meldungen/summary?year=${year}`).catch(() => ({ data: { rows: [] } })),
          api.get("/damages?status=open").catch(() => ({ data: [] })),
          api.get("/damages?status=in_progress").catch(() => ({ data: [] })),
          api.get("/inventory").catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        // Permis: reused helper from Personal
        const list = permisExpiringSoon(persons.data || [], employers.data || [], 14);
        setPermits(list);
        // Meldungen: subcontractori activi cu < 10 zile rămase
        const mRows = (meld.data?.rows || []).filter((r) => r.active && r.remaining_days < 10);
        mRows.sort((a, b) => a.remaining_days - b.remaining_days);
        setMeldungen(mRows);
        setDamages([...(dOpen.data || []), ...(dProg.data || [])]);

        // === PSA stock alerts: pentru fiecare (categorie, mărime) calculează stocul LIBER ===
        const byCatSize = new Map(); // "cat|size" -> qty
        for (const it of inv.data || []) {
          if (it.item_type !== "psa") continue;
          if (!PSA_SIZED_CATEGORIES.has(it.psa_category)) continue;
          if (it.assignee_person_id) continue; // skip items deja predate
          const size = String(it.psa_size || "").trim();
          if (!size) continue;
          const rawQty = parseFloat(it.quantity);
          const qty = isNaN(rawQty) ? 1 : rawQty;
          const key = `${it.psa_category}|${size}`;
          byCatSize.set(key, (byCatSize.get(key) || 0) + qty);
        }
        // Identifică toate cuplajele (categorie, mărime) care apar undeva (chiar și asignate)
        // ca să detectăm „epuizat 0" și „redus ≤2"
        const allCatSizes = new Set();
        for (const it of inv.data || []) {
          if (it.item_type !== "psa") continue;
          if (!PSA_SIZED_CATEGORIES.has(it.psa_category)) continue;
          const size = String(it.psa_size || "").trim();
          if (!size) continue;
          allCatSizes.add(`${it.psa_category}|${size}`);
        }
        const alerts = [];
        for (const key of allCatSizes) {
          const [cat, size] = key.split("|");
          const qty = byCatSize.get(key) || 0;
          if (qty === 0) alerts.push({ cat, size, qty, level: "out" });
          else if (qty <= 2) alerts.push({ cat, size, qty, level: "low" });
        }
        // Sortează: epuizate întâi, apoi după cantitate crescător, apoi după categorie
        alerts.sort((a, b) => {
          if (a.level !== b.level) return a.level === "out" ? -1 : 1;
          if (a.qty !== b.qty) return a.qty - b.qty;
          return a.cat.localeCompare(b.cat);
        });
        setPsaAlerts(alerts);

        setLoaded(true);
      } catch {
        setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin]);

  if (!isAdmin || !loaded) return null;

  const permitsExpired = permits.filter((x) => x.days_left < 0);
  const psaOutCount = psaAlerts.filter((a) => a.level === "out").length;
  const totalIssues = permits.length + meldungen.length + damages.length + psaAlerts.length;

  if (totalIssues === 0) return null;

  const hasCritical = permitsExpired.length > 0 || psaOutCount > 0;
  const tone = hasCritical
    ? "border-l-red-600 bg-red-50"
    : "border-l-amber-500 bg-amber-50";
  const iconTone = hasCritical ? "text-red-600" : "text-amber-600";
  const titleTone = hasCritical ? "text-red-700" : "text-amber-800";

  return (
    <div
      className={`mb-4 card-flat p-4 border-l-4 ${tone}`}
      data-testid="compliance-banner"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-start gap-3 text-left"
        data-testid="compliance-banner-toggle"
      >
        <ShieldAlert className={`${iconTone} flex-shrink-0 mt-0.5`} size={22} />
        <div className="flex-1">
          <div className={`font-bold text-sm ${titleTone}`}>
            Compliance · {totalIssues} {totalIssues === 1 ? "alertă" : "alerte"} active
          </div>
          <div className="text-xs text-zinc-700 mt-1 flex flex-wrap gap-x-4 gap-y-1">
            {permits.length > 0 && (
              <span className="flex items-center gap-1">
                <BadgeAlert size={13} className={permitsExpired.length > 0 ? "text-red-600" : "text-amber-600"} />
                <strong>{permits.length}</strong> permis(e) de ședere
                {permitsExpired.length > 0 && <span className="text-red-700"> · {permitsExpired.length} EXPIRAT(E)</span>}
              </span>
            )}
            {meldungen.length > 0 && (
              <span className="flex items-center gap-1">
                <Globe size={13} className="text-amber-600" />
                <strong>{meldungen.length}</strong> subcontractor(i) cu &lt;10 zile Meldeverfahren
              </span>
            )}
            {damages.length > 0 && (
              <span className="flex items-center gap-1">
                <Wrench size={13} className="text-zinc-700" />
                <strong>{damages.length}</strong> daună/daune deschise
              </span>
            )}
            {psaAlerts.length > 0 && (
              <span className="flex items-center gap-1" data-testid="compliance-psa-summary">
                <HardHat size={13} className={psaOutCount > 0 ? "text-red-600" : "text-amber-600"} />
                <strong>{psaAlerts.length}</strong> mărim{psaAlerts.length === 1 ? "e" : "i"} PSA cu stoc redus
                {psaOutCount > 0 && <span className="text-red-700"> · {psaOutCount} EPUIZAT{psaOutCount === 1 ? "Ă" : "E"}</span>}
              </span>
            )}
          </div>
        </div>
        <div className="flex-shrink-0 text-zinc-500 mt-1">
          {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>
      </button>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-zinc-200/70 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-xs">
          {/* Permise */}
          {permits.length > 0 && (
            <div data-testid="compliance-permits-section">
              <div className="font-bold uppercase tracking-wider text-[10px] text-zinc-600 mb-1.5 flex items-center gap-1">
                <BadgeAlert size={12} /> Permise ședere ({permits.length})
              </div>
              <ul className="space-y-1">
                {permits.slice(0, 8).map((x) => (
                  <li key={x.person_id} className="flex items-start gap-1.5">
                    <span className={x.days_left < 0 ? "text-red-700 font-bold whitespace-nowrap" : "text-amber-800 whitespace-nowrap"}>
                      {x.days_left < 0 ? `⛔ −${-x.days_left}z` : `⚠ ${x.days_left}z`}
                    </span>
                    <Link
                      to={`/personal/${x.person_id}#documente`}
                      className="hover:underline font-medium truncate"
                      data-testid={`compliance-permit-link-${x.person_id}`}
                    >
                      {x.person_name}
                    </Link>
                  </li>
                ))}
                {permits.length > 8 && (
                  <li className="text-zinc-500 italic">… și încă {permits.length - 8}</li>
                )}
              </ul>
            </div>
          )}

          {/* Meldeverfahren */}
          {meldungen.length > 0 && (
            <div data-testid="compliance-meldungen-section">
              <div className="font-bold uppercase tracking-wider text-[10px] text-zinc-600 mb-1.5 flex items-center gap-1">
                <Globe size={12} /> Meldeverfahren ({meldungen.length})
              </div>
              <ul className="space-y-1">
                {meldungen.slice(0, 8).map((m) => (
                  <li key={m.person_id} className="flex items-start gap-1.5">
                    <span className={m.remaining_days <= 0 ? "text-red-700 font-bold whitespace-nowrap" : "text-amber-800 whitespace-nowrap"}>
                      {m.remaining_days <= 0 ? "⛔ 0z" : `⚠ ${m.remaining_days}z`}
                    </span>
                    <Link
                      to={`/personal/${m.person_id}#meldeverfahren`}
                      className="hover:underline font-medium truncate"
                      data-testid={`compliance-meld-link-${m.person_id}`}
                    >
                      {m.name}
                    </Link>
                  </li>
                ))}
                {meldungen.length > 8 && (
                  <li className="text-zinc-500 italic">… și încă {meldungen.length - 8}</li>
                )}
              </ul>
            </div>
          )}

          {/* Daune */}
          {damages.length > 0 && (
            <div data-testid="compliance-damages-section">
              <div className="font-bold uppercase tracking-wider text-[10px] text-zinc-600 mb-1.5 flex items-center gap-1">
                <Wrench size={12} /> Daune deschise ({damages.length})
              </div>
              <ul className="space-y-1">
                {damages.slice(0, 8).map((d) => {
                  const sevColor = d.severity === "major" ? "text-red-700 font-bold"
                    : d.severity === "medium" ? "text-amber-700 font-semibold"
                    : "text-zinc-700";
                  return (
                    <li key={d.id} className="flex items-start gap-1.5">
                      <span className={`${sevColor} whitespace-nowrap`}>
                        {d.severity === "major" ? "⛔" : d.severity === "medium" ? "⚠" : "•"}
                      </span>
                      <Link
                        to={`/daune`}
                        className="hover:underline truncate"
                        data-testid={`compliance-damage-link-${d.id}`}
                      >
                        {d.description?.slice(0, 50) || "Daună fără descriere"}
                        {d.description?.length > 50 ? "…" : ""}
                      </Link>
                    </li>
                  );
                })}
                {damages.length > 8 && (
                  <li className="text-zinc-500 italic">… și încă {damages.length - 8}</li>
                )}
              </ul>
            </div>
          )}
          {/* PSA stock alerts */}
          {psaAlerts.length > 0 && (
            <div data-testid="compliance-psa-section">
              <div className="font-bold uppercase tracking-wider text-[10px] text-zinc-600 mb-1.5 flex items-center gap-1">
                <HardHat size={12} /> PSA stoc redus ({psaAlerts.length})
              </div>
              <ul className="space-y-1">
                {psaAlerts.slice(0, 8).map((a, idx) => (
                  <li key={`${a.cat}-${a.size}-${idx}`} className="flex items-start gap-1.5">
                    <span className={a.level === "out" ? "text-red-700 font-bold whitespace-nowrap" : "text-amber-800 whitespace-nowrap"}>
                      {a.level === "out" ? "🔴 0" : `🟡 ${a.qty}`}
                    </span>
                    <Link
                      to="/inventar?tab=psa"
                      className="hover:underline font-medium truncate"
                      data-testid={`compliance-psa-link-${a.cat}-${a.size}`}
                    >
                      {PSA_CAT_LABEL[a.cat] || a.cat} mărimea <strong>{a.size}</strong>
                    </Link>
                  </li>
                ))}
                {psaAlerts.length > 8 && (
                  <li className="text-zinc-500 italic">… și încă {psaAlerts.length - 8}</li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
